import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  IconButton,
  Slider,
  Stack,
  useTheme,
} from "@mui/material";
import Forward10Icon from "@mui/icons-material/Forward10";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Replay10Icon from "@mui/icons-material/Replay10";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VSNTypography from "../../shared/VSNTypography";
import { LearningStyle } from "./LearningStyle";
import VSNLoader from "../../shared/VSNLoader";

const formatTime = (time) =>
  time ? new Date(time * 1000).toISOString().substr(14, 5) : "00:00";

const LearningVideoPlayer = ({ data, loader }) => {
  const classes = LearningStyle();
  const videoRef = useRef(null);
  const youtubeRef = useRef(null);
  const containerRef = useRef(null);
  const theme = useTheme();

  const [player, setPlayer] = useState(null);
  const [isYouTube, setIsYouTube] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isYouTubeLink = (url) =>
    url?.includes("youtube.com") || url?.includes("youtu.be");

  useEffect(() => {
    setIsYouTube(isYouTubeLink(data?.link));
  }, [data]);

  useEffect(() => {
    let interval;
    if (playing && (videoRef.current || player)) {
      interval = setInterval(() => {
        const time = isYouTube
          ? player?.getCurrentTime?.()
          : videoRef.current?.currentTime;
        setCurrentTime(time || 0);
      }, 500);
    }
    return () => clearInterval(interval);
  }, [playing, isYouTube, player]);

  const getYouTubeVideoId = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname === "youtu.be"
        ? urlObj.pathname.slice(1)
        : urlObj.searchParams.get("v");
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!isYouTube || !youtubeRef.current || player) return;

    const loadYouTubeAPI = () => {
      return new Promise((resolve) => {
        if (window.YT && window.YT.Player) resolve();
        else {
          const tag = document.createElement("script");
          tag.src = "https://www.youtube.com/iframe_api";
          document.body.appendChild(tag);
          window.onYouTubeIframeAPIReady = () => resolve();
        }
      });
    };

    loadYouTubeAPI().then(() => {
      const videoId = getYouTubeVideoId(data.link);
      const ytPlayer = new window.YT.Player(youtubeRef.current, {
        videoId,
        playerVars: {
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
        },
        events: {
          onReady: (e) => {
            setDuration(e.target.getDuration());
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) setPlaying(true);
            if (e.data === window.YT.PlayerState.PAUSED) setPlaying(false);
          },
        },
      });

      setPlayer(ytPlayer);
    });
  }, [isYouTube, data?.link]);

  const togglePlay = () => {
    if (isYouTube) {
      if (playing) {
        player.pauseVideo();
      } else {
        player.playVideo();
      }
    } else {
      const video = videoRef.current;
      if (video) {
        if (playing) {
          video.pause();
        } else {
          video.play();
        }
      }
    }
    setPlaying(!playing);
  };

  const rewind = () => {
    if (isYouTube) {
      player.seekTo(Math.max(0, player.getCurrentTime() - 10), true);
    } else if (videoRef.current) {
      videoRef.current.currentTime -= 10;
    }
  };

  const forward = () => {
    if (isYouTube) {
      player.seekTo(player.getCurrentTime() + 10, true);
    } else if (videoRef.current) {
      videoRef.current.currentTime += 10;
    }
  };

  const handleSeek = (_, newValue) => {
    if (isYouTube) {
      player.seekTo(newValue, true);
    } else if (videoRef.current) {
      videoRef.current.currentTime = newValue;
    }
    setCurrentTime(newValue);
  };

  const handleFullscreen = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const handleControlVisibility = () => {
    setShowControls(true);
    clearTimeout(window.hideControlTimer);
    window.hideControlTimer = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  useEffect(() => {
    if (!isYouTube && videoRef.current) {
      videoRef.current.volume = volume;
      videoRef.current.muted = muted;
    } else if (isYouTube && player?.setVolume) {
      player.setVolume(volume * 100);
      if (muted) player.mute?.();
      else player.unMute?.();
    }
  }, [volume, muted, player, isYouTube]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  if (loader) return <VSNLoader variant="transParent" />;

  return (
    <Container maxWidth="xl" p={{ sm: 4, xs: 2 }}>
      <Box
        ref={containerRef}
        onMouseMove={handleControlVisibility}
        position="relative"
        className={classes.videoContainer}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          backgroundColor: "black",
        }}
        pt={3}
      >
        {isYouTube ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxHeight: "670px",
              paddingTop: isFullscreen ? "56.25%" : "42.5%",
              backgroundColor: "black",
            }}
          >
            <Box
              ref={youtubeRef}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
            />
          </Box>
        ) : (
          <video
            ref={videoRef}
            muted
            playsInline
            preload="true"
            onLoadedMetadata={() =>
              setDuration(videoRef.current?.duration || 0)
            }
            poster={data?.thumbnail}
            controls={false}
            style={{
              width: "100%",
              maxHeight: isFullscreen ? "100vh" : "670px",
              objectFit: "cover",
              backgroundColor: "black",
            }}
          >
            <source src={data?.link} type="video/mp4" />
          </video>
        )}

        {showControls && (
          <>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-between"
              position="absolute"
              bottom={10}
              left={0}
              right={0}
              px={{ sm: 4 }}
              zIndex={10}
              bgcolor="rgba(0,0,0,0.5)"
            >
              <Slider
                value={currentTime}
                min={0}
                max={duration}
                onChange={handleSeek}
                className={classes.videoSliderTrack}
              />
              <Stack direction="row" alignItems="center">
                <IconButton onClick={togglePlay} sx={{ color: "#fff" }}>
                  {playing ? (
                    <PauseIcon fontSize="large" />
                  ) : (
                    <PlayArrowIcon fontSize="large" />
                  )}
                </IconButton>

                <VSNTypography sx={{ color: "#fff", mx: 2 }}>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </VSNTypography>

                <IconButton
                  onClick={rewind}
                  sx={{ color: "#fff", display: { sm: "block", xs: "none" } }}
                >
                  <Replay10Icon fontSize="large" />
                </IconButton>

                <IconButton
                  onClick={forward}
                  sx={{ color: "#fff", display: { sm: "block", xs: "none" } }}
                >
                  <Forward10Icon fontSize="large" />
                </IconButton>

                <IconButton
                  onClick={() => setMuted(!muted)}
                  sx={{ color: "#fff" }}
                >
                  {muted || volume === 0 ? (
                    <VolumeOffIcon fontSize="large" />
                  ) : volume < 0.5 ? (
                    <VolumeDownIcon fontSize="large" />
                  ) : (
                    <VolumeUpIcon fontSize="large" />
                  )}
                </IconButton>

                <Slider
                  min={0}
                  max={1}
                  step={0.01}
                  value={muted ? 0 : volume}
                  onChange={(e, newValue) => {
                    setVolume(newValue);
                    if (newValue > 0) setMuted(false);
                  }}
                  sx={{ width: 80, color: "white" }}
                />
              </Stack>

              <IconButton onClick={handleFullscreen} sx={{ color: "#fff" }}>
                {isFullscreen ? (
                  <FullscreenExitIcon fontSize="large" />
                ) : (
                  <FullscreenIcon fontSize="large" />
                )}
              </IconButton>
            </Box>
          </>
        )}
      </Box>

      <Stack spacing={1} my={4}>
        {/* <VSNTypography color="var(--secondary)" fontWeight={700}>
          Personal Development for a Sa
        </VSNTypography> */}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <VSNTypography fontSize={{ md: 40, sm: 30, xs: 24 }} fontWeight={700}>
            {data?.title ?? ""}
          </VSNTypography>
          {/* <IconButton className={classes.addIcon}>
            <AddIcon fontSize="large" />
          </IconButton> */}
        </Stack>

        <VSNTypography
          fontSize={{ md: 24, sm: 20, xs: 18 }}
          fontWeight={400}
          color="text.secondary"
          dangerouslySetInnerHTML={{
            __html: data?.summary,
          }}
          pb={1}
        />

        <VSNTypography>
          Instructor(s):{" "}
          <VSNTypography sx={{ fontWeight: "600" }} component="strong">
            {data?.instructor ?? ""}
          </VSNTypography>
        </VSNTypography>
        <Stack
          direction="row"
          pt={2}
          gap={1}
          flexWrap="wrap"
          alignItems="center"
        >
          <VSNTypography mr={2} sx={{ flex: "none" }}>
            Topics Covered:
          </VSNTypography>
          <Stack direction="row" flexWrap="wrap" gap={1.5} flex={{ sm: 1 }}>
            {data?.tags?.map((tag, index) => (
              <VSNTypography
                key={index}
                sx={{
                  padding: "8px",
                  backgroundColor: "#FEF6EE",
                  borderRadius: "8px",
                  fontWeight: 700,
                  color: "var(--secondary)",
                }}
              >
                {tag}
              </VSNTypography>
            ))}
          </Stack>

          {/* <VSNButton variant="outlined" sx={{ px: 2, flex: "none" }}>
            Mark As Complete
          </VSNButton> */}
        </Stack>
      </Stack>
    </Container>
  );
};

export default LearningVideoPlayer;
